

import { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react'
import { FiVolume2, FiVolumeX, FiMaximize, FiMinimize, FiVolume1, FiRepeat, FiChevronLeft, FiChevronRight, FiSkipBack, FiSkipForward, FiInfo, FiCopy, FiExternalLink } from 'react-icons/fi'
import { IoPlay, IoPause } from 'react-icons/io5'
import { LuLoaderCircle } from "react-icons/lu";
import { Slider } from '@/components/ui/shadcn/slider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select'
import { trimVideo } from '@/lib/trimVideo'

const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
const DEFAULT_FPS = 30
const VOLUME_STORAGE_KEY = 'videoPlayerVolume'
const MUTED_STORAGE_KEY = 'videoPlayerMuted'

// Helper to get saved volume from localStorage
const getSavedVolume = () => {
    if (typeof window === 'undefined') return 1
    try {
        const saved = localStorage.getItem(VOLUME_STORAGE_KEY)
        if (saved !== null) {
            const vol = parseFloat(saved)
            if (!isNaN(vol) && vol >= 0 && vol <= 1) return vol
        }
    } catch (e) {
        // localStorage might be unavailable
    }
    return 1
}

// Helper to get saved muted state from localStorage
const getSavedMuted = () => {
    if (typeof window === 'undefined') return false
    try {
        const saved = localStorage.getItem(MUTED_STORAGE_KEY)
        if (saved !== null) {
            return saved === 'true'
        }
    } catch (e) {
        // localStorage might be unavailable
    }
    return false
}

const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Sub-second precise duration formatting used for the staged trim length, e.g.
// "1.24s" for short clips or "1:02.34" once it crosses a minute.
const formatPreciseTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0.00s'
    if (seconds < 60) return `${seconds.toFixed(2)}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toFixed(2).padStart(5, '0')}`
}

function VideoPlayer({
    src,
    asset,
    poster,
    autoPlay = false,
    muted = false,
    loop = false,
    startTime,
    allowFullscreen = true,
    showPlayOverlay = true,
    allowTrim = false,
    className = '',
    crossOrigin = 'anonymous',
    onPlay,
    onPause,
    onEnded,
    onTimeUpdate,
    onLoadedMetadata,
    ...props
}, ref) {
    // Asset URL takes priority over raw src
    const videoSrc = asset?.url || src

    const videoRef = useRef(null)
    const containerRef = useRef(null)
    const progressRef = useRef(null)
    const hideControlsTimeout = useRef(null)
    const animationFrameRef = useRef(null)
    const isFirstMount = useRef(true)
    const hasAppliedStartTime = useRef(false)

    const [isLoading, setIsLoading] = useState(true)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(() => muted || getSavedMuted())
    const [isLooping, setIsLooping] = useState(loop)
    const [volume, setVolume] = useState(() => (muted || getSavedMuted()) ? 0 : getSavedVolume())
    const [previousVolume, setPreviousVolume] = useState(() => getSavedVolume())
    const [currentTime, setCurrentTime] = useState(0)
    const [displayTime, setDisplayTime] = useState(0) // Separate state for smooth visual updates
    const [duration, setDuration] = useState(0)
    const [fps, setFps] = useState(null) // Detected frame rate (null until detected)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)
    const [showControls, setShowControls] = useState(true)
    const [isDragging, setIsDragging] = useState(false)
    const [isHovering, setIsHovering] = useState(false)
    const [buffered, setBuffered] = useState(0)
    const [showInfo, setShowInfo] = useState(false)
    const [videoResolution, setVideoResolution] = useState({ width: 0, height: 0 })
    const [progressWidth, setProgressWidth] = useState(0) // measured width of the scrub bar (for tick spacing)
    const [hoverTime, setHoverTime] = useState(null) // time under the cursor while hovering the scrub bar

    // Trim in/out points (seconds). Null until a duration is known. The kept
    // region is [inPoint, outPoint]. `trimDragging` tracks which handle (if any)
    // is currently being dragged on the scrub bar.
    const [inPoint, setInPoint] = useState(null)
    const [outPoint, setOutPoint] = useState(null)
    const [trimDragging, setTrimDragging] = useState(null) // 'in' | 'out' | null

    // Calculate frame time based on detected fps (fallback to DEFAULT_FPS if not detected)
    const frameTime = 1 / (fps || DEFAULT_FPS)

    // Play/Pause toggle
    const togglePlay = useCallback(() => {
        if (!videoRef.current) return
        if (isPlaying) {
            videoRef.current.pause()
        } else {
            videoRef.current.play()
        }
    }, [isPlaying])

    // Mute toggle
    const toggleMute = useCallback(() => {
        if (!videoRef.current) return
        if (isMuted) {
            videoRef.current.muted = false
            videoRef.current.volume = previousVolume
            setVolume(previousVolume)
            setIsMuted(false)
        } else {
            setPreviousVolume(volume)
            videoRef.current.muted = true
            videoRef.current.volume = 0
            setVolume(0)
            setIsMuted(true)
        }
    }, [isMuted, volume, previousVolume])

    // Loop toggle
    const toggleLoop = useCallback(() => {
        if (!videoRef.current) return
        const newLoopState = !isLooping
        videoRef.current.loop = newLoopState
        setIsLooping(newLoopState)
    }, [isLooping])

    // Frame skip
    const skipFrame = useCallback((direction) => {
        if (!videoRef.current) return
        const effFps = fps || DEFAULT_FPS
        // Identify the frame currently displayed, then jump to the CENTER of the
        // neighbouring frame. Stepping by frame index + seeking to frame centers
        // (instead of accumulating ±1/fps, which drifts and can land on ambiguous
        // frame boundaries) keeps stepping frame-accurate and consistent with how
        // the trim samples frames — so a cut you land on is the cut you get.
        const curFrame = Math.floor(videoRef.current.currentTime * effFps + 1e-6)
        const maxFrame = Math.max(0, Math.ceil(duration * effFps) - 1)
        const targetFrame = Math.min(maxFrame, Math.max(0, curFrame + direction))
        const newTime = Math.min(duration, (targetFrame + 0.5) / effFps)
        videoRef.current.currentTime = newTime
        setCurrentTime(newTime)
        setDisplayTime(newTime)
    }, [duration, fps])

    // Jump to the very first frame
    const goToStart = useCallback(() => {
        if (!videoRef.current) return
        videoRef.current.currentTime = 0
        setCurrentTime(0)
        setDisplayTime(0)
    }, [])

    // Jump to the very end of the video
    const goToEnd = useCallback(() => {
        if (!videoRef.current || !duration) return
        videoRef.current.currentTime = duration
        setCurrentTime(duration)
        setDisplayTime(duration)
    }, [duration])

    // Capture the current frame as an image Blob at the video's native resolution.
    // Returns a Promise<Blob>. Note: capturing a cross-origin video requires the
    // source to send permissive CORS headers (the video uses crossOrigin), otherwise
    // the canvas becomes tainted and this rejects with a SecurityError.
    const captureFrame = useCallback((type = 'image/png', quality) => {
        return new Promise((resolve, reject) => {
            const video = videoRef.current
            if (!video) {
                reject(new Error('Video element is not available'))
                return
            }
            const width = video.videoWidth
            const height = video.videoHeight
            if (!width || !height) {
                reject(new Error('Video has no dimensions yet'))
                return
            }
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            if (!ctx) {
                reject(new Error('Could not get 2D canvas context'))
                return
            }
            try {
                ctx.drawImage(video, 0, 0, width, height)
            } catch (err) {
                reject(err)
                return
            }
            canvas.toBlob(
                (blob) => {
                    if (blob) resolve(blob)
                    else reject(new Error('Failed to capture frame'))
                },
                type,
                quality,
            )
        })
    }, [])

    // Resolve the effective trim range, falling back to the full video when no
    // in/out points have been set.
    const getTrimRange = useCallback(() => {
        const start = inPoint != null ? inPoint : 0
        const end = outPoint != null ? outPoint : duration
        return {
            start,
            end,
            duration,
            hasSelection: (inPoint != null && inPoint > 0) || (outPoint != null && outPoint < duration),
        }
    }, [inPoint, outPoint, duration])

    // Imperatively trim the video to the given range (defaults to the current
    // in/out selection) and resolve with an MP4 Blob. Performed entirely client
    // side. Mirrors captureFrame's imperative shape.
    const trim = useCallback(async (startArg, endArg, options = {}) => {
        const range = getTrimRange()
        const start = startArg != null ? startArg : range.start
        const end = endArg != null ? endArg : range.end
        return trimVideo({
            src: videoSrc,
            crossOrigin,
            start,
            end,
            fps: fps || DEFAULT_FPS,
            ...options,
        })
    }, [getTrimRange, videoSrc, crossOrigin, fps])

    // Expose an imperative API to parent components via ref.
    useImperativeHandle(ref, () => ({
        captureFrame,
        trim,
        getTrimRange,
        getVideoElement: () => videoRef.current,
    }), [captureFrame, trim, getTrimRange])

    // Set the in point to the current playhead. If it lands past the current
    // out point, drag the out point along so it never sits before the in point.
    const setInToCurrent = useCallback(() => {
        if (!duration) return
        const next = Math.max(0, Math.min(currentTime, duration))
        setInPoint(next)
        setOutPoint((prevOut) => (prevOut == null || next > prevOut ? next : prevOut))
    }, [duration, currentTime])

    // Set the out point to the current playhead. If it lands before the current
    // in point, drag the in point along so it never sits after the out point.
    const setOutToCurrent = useCallback(() => {
        if (!duration) return
        const next = Math.max(0, Math.min(currentTime, duration))
        setOutPoint(next)
        setInPoint((prevIn) => (prevIn == null || next < prevIn ? next : prevIn))
    }, [duration, currentTime])

    // Volume change
    const handleVolumeChange = useCallback((value) => {
        if (!videoRef.current) return
        const newVolume = value[0]
        videoRef.current.volume = newVolume
        videoRef.current.muted = newVolume === 0
        setVolume(newVolume)
        setIsMuted(newVolume === 0)
        if (newVolume > 0) {
            setPreviousVolume(newVolume)
        }
    }, [])

    // Fullscreen toggle
    const toggleFullscreen = useCallback(async () => {
        if (!containerRef.current || !allowFullscreen) return
        
        try {
            if (!document.fullscreenElement) {
                await containerRef.current.requestFullscreen()
            } else {
                await document.exitFullscreen()
            }
        } catch (err) {
            console.error('Fullscreen error:', err)
        }
    }, [allowFullscreen])

    // Playback speed change
    const handleSpeedChange = useCallback((value) => {
        if (!videoRef.current) return
        const speed = parseFloat(value)
        videoRef.current.playbackRate = speed
        setPlaybackSpeed(speed)
    }, [])

    // Calculate time from mouse position
    const getTimeFromMouseEvent = useCallback((e) => {
        if (!progressRef.current || !duration) return 0
        const rect = progressRef.current.getBoundingClientRect()
        const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        return pos * duration
    }, [duration])

    // Progress bar click/drag - immediate visual update
    const handleProgressMouseDown = useCallback((e) => {
        e.preventDefault()
        const newTime = getTimeFromMouseEvent(e)
        setIsDragging(true)
        setDisplayTime(newTime) // Immediate visual feedback
        // Pause video when starting to scrub
        if (videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause()
        }
    }, [getTimeFromMouseEvent])

    const handleProgressMouseMove = useCallback((e) => {
        if (!isDragging) return
        const newTime = getTimeFromMouseEvent(e)
        setDisplayTime(newTime) // Immediate visual feedback during drag
        // Update video while scrubbing
        if (videoRef.current) {
            videoRef.current.currentTime = newTime
        }
    }, [isDragging, getTimeFromMouseEvent])

    const handleProgressMouseUp = useCallback((e) => {
        if (!isDragging) return
        const newTime = getTimeFromMouseEvent(e)
        setIsDragging(false)
        // Now actually seek the video
        if (videoRef.current) {
            videoRef.current.currentTime = newTime
            setCurrentTime(newTime)
            setDisplayTime(newTime)
        }
    }, [isDragging, getTimeFromMouseEvent])

    // Track the time under the cursor for the hover preview line / tooltip
    const handleProgressHover = useCallback((e) => {
        setHoverTime(getTimeFromMouseEvent(e))
    }, [getTimeFromMouseEvent])

    const handleProgressHoverLeave = useCallback(() => {
        setHoverTime(null)
    }, [])

    // Begin dragging an in/out trim handle. Stop propagation so the underlying
    // scrub bar doesn't also start seeking.
    const handleTrimHandleMouseDown = useCallback((which) => (e) => {
        e.preventDefault()
        e.stopPropagation()
        setTrimDragging(which)
    }, [])

    const handleTrimDragMove = useCallback((e) => {
        if (!trimDragging) return
        const t = getTimeFromMouseEvent(e)
        if (trimDragging === 'in') {
            setInPoint(Math.max(0, Math.min(t, (outPoint ?? duration) - frameTime)))
        } else {
            setOutPoint(Math.min(duration, Math.max(t, (inPoint ?? 0) + frameTime)))
        }
    }, [trimDragging, getTimeFromMouseEvent, inPoint, outPoint, duration, frameTime])

    const handleTrimDragEnd = useCallback(() => {
        setTrimDragging(null)
    }, [])

    // Smooth progress update using requestAnimationFrame
    const updateProgress = useCallback(() => {
        if (!videoRef.current || isDragging) return
        
        const time = videoRef.current.currentTime
        setCurrentTime(time)
        setDisplayTime(time)
        
        // Update buffered
        if (videoRef.current.buffered.length > 0) {
            const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1)
            setBuffered((bufferedEnd / videoRef.current.duration) * 100)
        }
        
        onTimeUpdate?.(time)
        
        // Continue the animation loop if playing
        if (!videoRef.current.paused && !videoRef.current.ended) {
            animationFrameRef.current = requestAnimationFrame(updateProgress)
        }
    }, [isDragging, onTimeUpdate])

    // Video event handlers
    const handlePlay = useCallback(() => {
        setIsPlaying(true)
        setIsLoading(false) // Video is actually playing now
        // Start smooth progress updates
        animationFrameRef.current = requestAnimationFrame(updateProgress)
        onPlay?.()
    }, [onPlay, updateProgress])

    const handlePause = useCallback(() => {
        setIsPlaying(false)
        // Stop animation loop
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        onPause?.()
    }, [onPause])

    const handleTimeUpdate = useCallback(() => {
        // Only update buffered here, progress is handled by requestAnimationFrame
        if (!videoRef.current) return
        if (videoRef.current.buffered.length > 0) {
            const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1)
            setBuffered((bufferedEnd / videoRef.current.duration) * 100)
        }
    }, [])

    const handleLoadedMetadata = useCallback(() => {
        if (!videoRef.current) return
        setDuration(videoRef.current.duration)
        setVideoResolution({
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight
        })
        // Seed the trim selection to the full clip so the in/out handles are
        // visible and draggable from the start.
        if (allowTrim) {
            setInPoint(0)
            setOutPoint(videoRef.current.duration)
        }
        // Apply start time if provided and not already applied
        if (startTime != null && !hasAppliedStartTime.current) {
            const clampedTime = Math.max(0, Math.min(startTime, videoRef.current.duration))
            videoRef.current.currentTime = clampedTime
            setCurrentTime(clampedTime)
            setDisplayTime(clampedTime)
            hasAppliedStartTime.current = true
        }
        onLoadedMetadata?.(videoRef.current.duration)
    }, [onLoadedMetadata, startTime, allowTrim])

    const handleCanPlay = useCallback(() => {
        // For non-autoplay videos, hide loader when video is ready to play
        if (!autoPlay) {
            setIsLoading(false)
        }
    }, [autoPlay])

    // Fallback: also handle loadeddata for cases where canplay doesn't fire
    const handleLoadedData = useCallback(() => {
        if (!autoPlay) {
            setIsLoading(false)
        }
    }, [autoPlay])

    const handleEnded = useCallback(() => {
        setIsPlaying(false)
        // Stop animation loop
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        onEnded?.()
    }, [onEnded])

    // Controls visibility
    const showControlsTemporarily = useCallback(() => {
        setShowControls(true)
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current)
        }
        if (isPlaying && !isHovering) {
            hideControlsTimeout.current = setTimeout(() => {
                setShowControls(false)
            }, 3000)
        }
    }, [isPlaying, isHovering])

    const handleMouseEnter = useCallback(() => {
        setIsHovering(true)
        setShowControls(true)
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current)
        }
    }, [])

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false)
        if (isPlaying) {
            hideControlsTimeout.current = setTimeout(() => {
                setShowControls(false)
            }, 1500)
        }
    }, [isPlaying])

    const handleMouseMove = useCallback(() => {
        showControlsTemporarily()
    }, [showControlsTemporarily])

    // Keyboard controls
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case ' ':
            case 'k':
                e.preventDefault()
                togglePlay()
                break
            case 'f':
                e.preventDefault()
                toggleFullscreen()
                break
            case 'm':
                e.preventDefault()
                toggleMute()
                break
            case 'l':
                e.preventDefault()
                toggleLoop()
                break
            case 'i':
                e.preventDefault()
                setShowInfo(prev => !prev)
                break
            case 'ArrowLeft':
                e.preventDefault()
                skipFrame(-1)
                break
            case 'ArrowRight':
                e.preventDefault()
                skipFrame(1)
                break
            case 'Home':
                e.preventDefault()
                goToStart()
                break
            case 'End':
                e.preventDefault()
                goToEnd()
                break
            case 'ArrowUp':
                e.preventDefault()
                handleVolumeChange([Math.min(1, volume + 0.1)])
                break
            case 'ArrowDown':
                e.preventDefault()
                handleVolumeChange([Math.max(0, volume - 0.1)])
                break
            // Set in/out points. Match on the physical bracket keys (via e.code)
            // so it fires whether or not Shift is held — i.e. both "[" and "{"
            // trigger the in point, "]" and "}" trigger the out point.
            case '[':
            case '{':
                if (allowTrim) {
                    e.preventDefault()
                    setInToCurrent()
                }
                break
            case ']':
            case '}':
                if (allowTrim) {
                    e.preventDefault()
                    setOutToCurrent()
                }
                break
            default:
                if (allowTrim && (e.code === 'BracketLeft' || e.code === 'BracketRight')) {
                    e.preventDefault()
                    if (e.code === 'BracketLeft') setInToCurrent()
                    else setOutToCurrent()
                }
                break
        }
    }, [togglePlay, toggleFullscreen, toggleMute, toggleLoop, skipFrame, goToStart, goToEnd, handleVolumeChange, volume, allowTrim, setInToCurrent, setOutToCurrent])

    // Fullscreen change listener
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }, [])

    // Mouse move/up listeners for dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleProgressMouseMove)
            window.addEventListener('mouseup', handleProgressMouseUp)
        }
        return () => {
            window.removeEventListener('mousemove', handleProgressMouseMove)
            window.removeEventListener('mouseup', handleProgressMouseUp)
        }
    }, [isDragging, handleProgressMouseMove, handleProgressMouseUp])

    // Mouse move/up listeners while dragging a trim handle
    useEffect(() => {
        if (trimDragging) {
            window.addEventListener('mousemove', handleTrimDragMove)
            window.addEventListener('mouseup', handleTrimDragEnd)
        }
        return () => {
            window.removeEventListener('mousemove', handleTrimDragMove)
            window.removeEventListener('mouseup', handleTrimDragEnd)
        }
    }, [trimDragging, handleTrimDragMove, handleTrimDragEnd])

    // Measure the scrub bar width so tick spacing adapts to the available room.
    // Depends on isLoading because the controls bar (and thus progressRef) only
    // mounts once loading finishes.
    useEffect(() => {
        const el = progressRef.current
        if (!el) return
        const measure = () => setProgressWidth(el.getBoundingClientRect().width)
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [isLoading])

    // Cleanup timeout and animation frame on unmount
    useEffect(() => {
        return () => {
            if (hideControlsTimeout.current) {
                clearTimeout(hideControlsTimeout.current)
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [])

    // Sync muted prop (skip initial mount to preserve localStorage state)
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false
            // On first mount, sync video element with initial state
            if (videoRef.current) {
                videoRef.current.muted = isMuted
                videoRef.current.volume = isMuted ? 0 : volume
            }
            return
        }
        // After first mount, respond to muted prop changes
        if (videoRef.current) {
            videoRef.current.muted = muted
            setIsMuted(muted)
            if (muted) setVolume(0)
        }
    }, [muted])

    // Set initial volume on video element and persist volume changes
    useEffect(() => {
        if (videoRef.current && !isMuted) {
            videoRef.current.volume = volume
        }
        // Save volume to localStorage (only if not muted to preserve the actual volume preference)
        if (volume > 0) {
            try {
                localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString())
            } catch (e) {
                // localStorage might be unavailable
            }
        }
    }, [volume, isMuted])

    // Persist mute state changes
    useEffect(() => {
        try {
            localStorage.setItem(MUTED_STORAGE_KEY, isMuted.toString())
        } catch (e) {
            // localStorage might be unavailable
        }
    }, [isMuted])

    // Reset state when source changes
    useEffect(() => {
        setIsLoading(true)
        setIsPlaying(false)
        setCurrentTime(0)
        setDisplayTime(0)
        setDuration(0)
        setFps(null)
        setVideoResolution({ width: 0, height: 0 })
        setInPoint(null)
        setOutPoint(null)
        setTrimDragging(null)
        hasAppliedStartTime.current = false
        // Cancel any pending animation frame
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        // Explicitly reload the video element to ensure new source is loaded
        if (videoRef.current) {
            videoRef.current.load()
        }
    }, [videoSrc])

    // Capture video resolution when video is ready (handles case where metadata already loaded)
    useEffect(() => {
        const video = videoRef.current
        if (!video) return

        const updateResolution = () => {
            if (video.videoWidth && video.videoHeight) {
                setVideoResolution({
                    width: video.videoWidth,
                    height: video.videoHeight
                })
            }
        }

        // Check if metadata already loaded
        if (video.readyState >= 1) {
            updateResolution()
        }

        // Also listen for loadedmetadata in case it fires later
        video.addEventListener('loadedmetadata', updateResolution)
        return () => video.removeEventListener('loadedmetadata', updateResolution)
    }, [videoSrc])

    // Detect video FPS using requestVideoFrameCallback
    useEffect(() => {
        const video = videoRef.current
        if (!video || !('requestVideoFrameCallback' in video)) return

        let fpsCallbackId = null
        let detectionComplete = false
        let startMediaTime = null
        let startFrameCount = null

        const detectFps = (now, metadata) => {
            if (detectionComplete) return
            
            // Use metadata.mediaTime and metadata.presentedFrames for accurate FPS calculation
            if (startMediaTime === null) {
                startMediaTime = metadata.mediaTime
                startFrameCount = metadata.presentedFrames
                fpsCallbackId = video.requestVideoFrameCallback(detectFps)
                return
            }

            const mediaTimeDelta = metadata.mediaTime - startMediaTime
            const framesDelta = metadata.presentedFrames - startFrameCount

            // Wait until we have enough data (at least 0.15 seconds of playback)
            if (mediaTimeDelta >= 0.15 && framesDelta >= 3) {
                const detectedFps = framesDelta / mediaTimeDelta
                // Round to common fps values
                const commonFps = [24, 25, 30, 48, 50, 60, 120]
                const closestFps = commonFps.reduce((prev, curr) => 
                    Math.abs(curr - detectedFps) < Math.abs(prev - detectedFps) ? curr : prev
                )
                setFps(closestFps)
                detectionComplete = true
                return
            }

            // Continue detecting
            fpsCallbackId = video.requestVideoFrameCallback(detectFps)
        }

        // Start detection when video plays
        const startDetection = () => {
            startMediaTime = null
            startFrameCount = null
            detectionComplete = false
            fpsCallbackId = video.requestVideoFrameCallback(detectFps)
        }

        video.addEventListener('play', startDetection)

        // If video is already playing, start immediately
        if (!video.paused) {
            startDetection()
        }

        return () => {
            video.removeEventListener('play', startDetection)
            detectionComplete = true
            if (fpsCallbackId && 'cancelVideoFrameCallback' in video) {
                video.cancelVideoFrameCallback(fpsCallbackId)
            }
        }
    }, [videoSrc])

    // Use displayTime for smooth visual progress (updated via RAF or during scrubbing)
    const progress = duration > 0 ? (displayTime / duration) * 100 : 0

    // Compute evenly-spaced "nice" tick marks for the scrub bar. Major ticks get
    // a timestamp label; minor ticks are short, unlabeled subdivisions.
    const { majorTicks, minorTicks, tickInterval } = useMemo(() => {
        if (!duration || !progressWidth) return { majorTicks: [], minorTicks: [], tickInterval: 0 }
        const minPxPerLabel = 64
        const maxLabels = Math.max(2, Math.floor(progressWidth / minPxPerLabel))
        const rawInterval = duration / maxLabels
        const niceIntervals = [1, 2, 5, 10, 15, 20, 30, 60, 120, 300, 600, 900, 1800, 3600]
        const interval = niceIntervals.find((i) => i >= rawInterval) ?? niceIntervals[niceIntervals.length - 1]
        const major = []
        for (let t = 0; t <= duration + 1e-6; t += interval) {
            major.push(Math.min(t, duration))
        }
        const minorInterval = interval / 4
        const minor = []
        for (let t = minorInterval; t < duration - 1e-6; t += minorInterval) {
            // Skip positions that coincide with a major tick
            if (Math.abs(t / interval - Math.round(t / interval)) < 1e-6) continue
            minor.push(t)
        }
        return { majorTicks: major, minorTicks: minor, tickInterval: interval }
    }, [duration, progressWidth])

    // Horizontal alignment for a tick label so edge labels stay inside the bar
    const tickLabelTransform = (pct) => {
        if (pct <= 2) return 'translateX(0)'
        if (pct >= 98) return 'translateX(-100%)'
        return 'translateX(-50%)'
    }

    const VolumeIcon = isMuted || volume === 0 
        ? FiVolumeX 
        : volume < 0.5 
            ? FiVolume1 
            : FiVolume2

    return (
        <div
            ref={containerRef}
            className={`relative flex flex-col bg-black overflow-hidden group select-none outline-none focus:outline-none focus-visible:outline-none ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            {/* Video area - flexes to fill the space above the controls so the whole frame stays visible */}
            <div className="relative flex-1 min-h-0">
            {/* Video Element */}
            <video
                ref={videoRef}
                src={videoSrc}
                poster={poster}
                autoPlay={autoPlay}
                loop={isLooping}
                crossOrigin={crossOrigin}
                playsInline
                className="w-full h-full object-contain"
                onPlay={handlePlay}
                onPause={handlePause}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onCanPlay={handleCanPlay}
                onLoadedData={handleLoadedData}
                onEnded={handleEnded}
                onClick={togglePlay}
                {...props}
            />

            {/* Loading overlay */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <LuLoaderCircle className="w-10 h-10 text-primary animate-spin" />
                </div>
            )}

            {/* Info Card */}
            {showInfo && (
                <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm rounded-lg p-3 text-xs text-white font-mono border border-secondary-foreground/20">
                    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                        <span className="text-secondary-foreground/60">Resolution</span>
                        <span>{videoResolution.width} × {videoResolution.height}</span>
                        
                        <span className="text-secondary-foreground/60">Duration</span>
                        <span>{Math.round(duration * 1000)}ms <span className="text-secondary-foreground/60">({formatTime(duration)})</span></span>
                        
                        <span className="text-secondary-foreground/60">Current Time</span>
                        <span>{Math.round(displayTime * 1000)}ms <span className="text-secondary-foreground/60">({formatTime(displayTime)})</span></span>
                        
                        <span className="text-secondary-foreground/60">FPS</span>
                        <span className={`${fps ? '' : 'text-secondary-foreground/40 italic text-xs'}`}>{fps ?? 'Play to detect FPS'}</span>
                        
                        <span className="text-secondary-foreground/60">Speed</span>
                        <span>{playbackSpeed}x</span>
                        
                        <span className="text-secondary-foreground/60">Source</span>
                        <div className="flex items-center gap-2">
                            <span 
                                className="truncate max-w-[150px] direction-rtl text-left" 
                                dir="rtl"
                                title={videoSrc}
                            >
                                {videoSrc || 'N/A'}
                            </span>
                            {videoSrc && (
                                <>
                                    <button
                                        onClick={() => navigator.clipboard.writeText(videoSrc)}
                                        className="p-1 rounded hover:bg-secondary-foreground/20 text-secondary-foreground/60 hover:text-white transition-colors flex-shrink-0 outline-none cursor-pointer"
                                        aria-label="Copy source URL"
                                    >
                                        <FiCopy className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => window.open(videoSrc, '_blank')}
                                        className="p-1 rounded hover:bg-secondary-foreground/20 text-secondary-foreground/60 hover:text-white transition-colors flex-shrink-0 outline-none cursor-pointer"
                                        aria-label="Open in new tab"
                                    >
                                        <FiExternalLink className="w-3 h-3" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            </div>

            {/* Controls Bar - always rendered below the video on a solid black background */}
            {!isLoading && (
            <div 
                className="shrink-0 bg-black pt-3 pb-2 px-3"
            >
                {/* Progress Bar - scrub track with timestamps, ticks & vertical play-head.
                    Layout (top → bottom): timestamp labels, then the 28px track. Ticks sit at
                    the bottom of the track; the buffered range is a thin line along the top. */}
                <div 
                    ref={progressRef}
                    className="relative h-10 mb-2 cursor-pointer select-none group/progress"
                    onMouseDown={handleProgressMouseDown}
                    onMouseMove={handleProgressHover}
                    onMouseLeave={handleProgressHoverLeave}
                >
                    {/* Timestamp labels (top) */}
                    {duration > 0 && majorTicks.map((t) => {
                        const pct = (t / duration) * 100
                        return (
                            <span
                                key={`label-${t}`}
                                className="absolute top-0 text-[10px] leading-none font-mono text-white/50 whitespace-nowrap pointer-events-none"
                                style={{ left: `${pct}%`, transform: tickLabelTransform(pct) }}
                            >
                                {formatTime(t)}
                            </span>
                        )
                    })}

                    {/* Full-width track background — breaks out of the controls-bar padding (px-3)
                        so the track BG + buffered line span the entire width, while the ticks,
                        labels and play-head (positioned in the padded progress container) stay
                        inset and aligned with the scrubbing area. */}
                    <div className="absolute bottom-0 -left-3 -right-3 h-6 bg-secondary-foreground/10 pointer-events-none" />

                    {/* Buffered — thin line along the bottom edge of the track, spanning full width */}
                    <div className="absolute -left-3 -right-3 bottom-0 h-[1px] pointer-events-none">
                        <div className="h-full bg-primary/30" style={{ width: `${buffered}%` }} />
                    </div>

                    {/* Minor ticks - aligned to the top of the track */}
                    {duration > 0 && minorTicks.map((t) => (
                        <div
                            key={`minor-${t}`}
                            className="absolute top-4 w-px h-2 bg-white/20 pointer-events-none"
                            style={{ left: `${(t / duration) * 100}%` }}
                        />
                    ))}

                    {/* Major ticks - aligned to the top of the track */}
                    {duration > 0 && majorTicks.map((t) => (
                        <div
                            key={`major-${t}`}
                            className="absolute top-4 w-px h-3 bg-white/40 pointer-events-none"
                            style={{ left: `${(t / duration) * 100}%` }}
                        />
                    ))}

                    {/* Start / end boundary markers — full-height ticks that show where the video
                        actually begins and ends, since the track background extends full width */}
                    {duration > 0 && (
                        <>
                            <div className="absolute top-4 h-6 w-[2px] bg-secondary-foreground/40 pointer-events-none" style={{ left: '0%' }} />
                            <div className="absolute top-4 h-6 w-[2px] bg-secondary-foreground/40 pointer-events-none" style={{ left: '100%', transform: 'translateX(-2px)' }} />
                        </>
                    )}

                    {/* Hover preview line + tooltip */}
                    {hoverTime != null && duration > 0 && !isDragging && (
                        <div
                            className="absolute bottom-0 h-6 w-px bg-white/50 pointer-events-none"
                            style={{ left: `${(hoverTime / duration) * 100}%` }}
                        >
                            <span
                                className="absolute -top-5 px-1.5 py-0.5 rounded bg-black/80 text-white text-[10px] font-mono whitespace-nowrap"
                                style={{ transform: tickLabelTransform((hoverTime / duration) * 100) }}
                            >
                                {formatTime(hoverTime)}
                            </span>
                        </div>
                    )}

                    {/* Trim selection — dims the discarded regions, highlights the
                        kept range, and exposes two draggable in/out handles. */}
                    {allowTrim && duration > 0 && inPoint != null && outPoint != null && (
                        <>
                            {/* Discarded region before the in-point */}
                            <div
                                className="absolute bottom-0 h-6 bg-black/60 pointer-events-none"
                                style={{ left: '0%', width: `${(inPoint / duration) * 100}%` }}
                            />
                            {/* Discarded region after the out-point */}
                            <div
                                className="absolute bottom-0 h-6 bg-black/60 pointer-events-none"
                                style={{ left: `${(outPoint / duration) * 100}%`, right: '0%' }}
                            />
                            {/* Kept range highlight */}
                            <div
                                className="absolute bottom-0 h-6 bg-amber-400/15 border-y border-amber-400/40 pointer-events-none"
                                style={{
                                    left: `${(inPoint / duration) * 100}%`,
                                    width: `${((outPoint - inPoint) / duration) * 100}%`,
                                }}
                            />
                            {/* In handle */}
                            <div
                                className="absolute top-4 h-6 w-[3px] bg-amber-400 cursor-ew-resize z-10"
                                style={{ left: `${(inPoint / duration) * 100}%`, transform: 'translateX(-1px)' }}
                                onMouseDown={handleTrimHandleMouseDown('in')}
                                title="Trim start"
                            />
                            {/* Out handle */}
                            <div
                                className="absolute top-4 h-6 w-[3px] bg-amber-400 cursor-ew-resize z-10"
                                style={{ left: `${(outPoint / duration) * 100}%`, transform: 'translateX(-2px)' }}
                                onMouseDown={handleTrimHandleMouseDown('out')}
                                title="Trim end"
                            />
                        </>
                    )}

                    {/* Vertical play-head */}
                    <div 
                        className="absolute bottom-0 h-6 w-[2px] bg-primary pointer-events-none"
                        style={{ left: `${progress}%`, transform: 'translateX(-1px)' }}
                    />
                </div>

                {/* Controls Row */}
                <div className="relative flex items-center justify-between gap-2">
                    {/* Left Controls */}
                    <div className="flex items-center gap-1">
                        {/* Volume */}
                        <div className="flex items-center gap-1 group/volume">
                            <button
                                onClick={toggleMute}
                                className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                                aria-label={isMuted ? 'Unmute' : 'Mute'}
                            >
                                <VolumeIcon className="w-5 h-5" />
                            </button>
                            <div className="w-0 overflow-hidden group-hover/volume:w-20 transition-all duration-200">
                                <Slider
                                    value={[volume]}
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    onValueChange={handleVolumeChange}
                                    className="w-20"
                                />
                            </div>
                        </div>

                        {/* Time */}
                        <div className="text-white text-xs font-mono ml-1">
                            <span>{formatTime(displayTime)}</span>
                            <span className="text-secondary-foreground/60 mx-1">/</span>
                            <span className="text-secondary-foreground/80">{formatTime(duration)}</span>
                        </div>

                        {/* Trim length — only shown when the selection is shorter
                            than the full clip (i.e. an actual trim is staged). */}
                        {allowTrim && inPoint != null && outPoint != null && (outPoint - inPoint) < duration - 1e-3 && (
                            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-secondary-foreground/20">
                                <span className="text-amber-400/90 text-xs font-mono whitespace-nowrap">
                                    {formatPreciseTime(Math.max(0, outPoint - inPoint))}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Center Controls - Absolute centered */}
                    <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1">
                        {/* Set Trim Start (in point) */}
                        {allowTrim && (
                            <button
                                onClick={setInToCurrent}
                                className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer font-mono text-lg leading-none"
                                aria-label="Set trim start to current time"
                                title="Set trim start (in point)"
                            >
                                {'{'}
                            </button>
                        )}

                        {/* Go to First Frame */}
                        <button
                            onClick={goToStart}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label="Go to First Frame"
                            title="Go to First Frame (Home)"
                        >
                            <FiSkipBack className="w-5 h-5" />
                        </button>

                        {/* Frame Back */}
                        <button
                            onClick={() => skipFrame(-1)}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label="Previous Frame"
                            title="Previous Frame (←)"
                        >
                            <FiChevronLeft className="w-5 h-5" />
                        </button>

                        {/* Play/Pause */}
                        <button
                            onClick={togglePlay}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <IoPause className="w-5 h-5" />
                            ) : (
                                <IoPlay className="w-5 h-5" />
                            )}
                        </button>

                        {/* Frame Forward */}
                        <button
                            onClick={() => skipFrame(1)}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label="Next Frame"
                            title="Next Frame (→)"
                        >
                            <FiChevronRight className="w-5 h-5" />
                        </button>

                        {/* Go to Last Frame */}
                        <button
                            onClick={goToEnd}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label="Go to Last Frame"
                            title="Go to Last Frame (End)"
                        >
                            <FiSkipForward className="w-5 h-5" />
                        </button>

                        {/* Set Trim End (out point) */}
                        {allowTrim && (
                            <button
                                onClick={setOutToCurrent}
                                className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer font-mono text-lg leading-none"
                                aria-label="Set trim end to current time"
                                title="Set trim end (out point)"
                            >
                                {'}'}
                            </button>
                        )}
                    </div>

                    {/* Right Controls */}
                    <div className="flex items-center gap-1">
                        {/* Info */}
                        <button
                            onClick={() => setShowInfo(!showInfo)}
                            className={`p-1.5 rounded hover:bg-secondary-foreground/20 transition-colors outline-none cursor-pointer ${showInfo ? 'text-primary' : 'text-white'}`}
                            aria-label={showInfo ? 'Hide Info' : 'Show Info'}
                        >
                            <FiInfo className="w-4 h-4" />
                        </button>

                        {/* Loop */}
                        <button
                            onClick={toggleLoop}
                            className={`p-1.5 rounded hover:bg-secondary-foreground/20 transition-colors outline-none cursor-pointer ${isLooping ? 'text-primary' : 'text-white'}`}
                            aria-label={isLooping ? 'Disable Loop' : 'Enable Loop'}
                        >
                            <FiRepeat className="w-4 h-4" />
                        </button>

                        {/* Playback Speed */}
                        <Select value={playbackSpeed.toString()} onValueChange={handleSpeedChange}>
                            <SelectTrigger className="h-7 w-16 bg-transparent border-none text-white text-xs hover:bg-secondary-foreground/20 focus:ring-0 focus:ring-offset-0 outline-none cursor-pointer">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent side="top" className="bg-popover border-border" container={containerRef.current}>
                                {PLAYBACK_SPEEDS.map((speed) => (
                                    <SelectItem key={speed} value={speed.toString()} className="text-xs">
                                        {`${speed}x`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Fullscreen */}
                        {allowFullscreen && (
                            <button
                                onClick={toggleFullscreen}
                                className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                            >
                                {isFullscreen ? (
                                    <FiMinimize className="w-5 h-5" />
                                ) : (
                                    <FiMaximize className="w-5 h-5" />
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    )
}

export default forwardRef(VideoPlayer)
